import { inject, Injectable } from "@angular/core";
import { BiTranslateService } from "@globals/bi-translate";
import { landLineLength_FI, landLineLength_SE, phoneLength_DE, phoneLength_DK, phoneLength_FI, phoneLength_NO, phoneLength_SE } from "@globals/classes/global-constants";
import { BiCountryId, BiLanguageId } from "@globals/enums/BiLanguageAndCountryId";

export interface LanguageConfig {
  dateFormat: string;
  dateTimeFormat: string;
  dateFormatNoYear: string;
  dateTimeFormatWithDayOfTheWeek: string;
  phoneCountryCode: number;
  phoneLength: number;

  /**
   * Min length of landline numbers
   */
  phoneLengthLandlineMin: number;
  /**
   * Max length of land line numbers
   */
  phoneLengthLandlineMax: number;
  minimumPhoneLength?: number;

  defaultMapCenterLatitude: number;
  defaultMapCenterLongitude: number;
  countryCode: string;
}

@Injectable()
export class BiLocalizationHelperBaseService {
  protected translator: BiTranslateService;

  constructor() {
    this.translator = inject(BiTranslateService);
  }
  /**
   * Returns language configurations based on a language id. By default, Danish countryId is used (if null or undefined
   * is passed).
   */
  public getLanguageConfigByCountry(countryId: BiCountryId): LanguageConfig {
    if (!countryId) countryId = BiCountryId.DK;

    switch (countryId) {
      case BiCountryId.DK:
        return {
          dateFormat: "DD/MM/YYYY",
          dateFormatNoYear: "DD/MM",
          dateTimeFormat: "DD/MM/YYYY HH:mm",
          dateTimeFormatWithDayOfTheWeek: "ddd. D. MMM, HH:mm",
          phoneCountryCode: 45,
          minimumPhoneLength: 8,
          phoneLength: 8,
          phoneLengthLandlineMin: phoneLength_DK,
          phoneLengthLandlineMax: phoneLength_DK,
          defaultMapCenterLatitude: 56.2639,
          defaultMapCenterLongitude: 9.5018,
          countryCode: "dk"
        };
      case BiCountryId.SE:
        return {
          dateFormat: "YYYY-MM-DD",
          dateFormatNoYear: "MM-DD",
          dateTimeFormat: "YYYY-MM-DD HH:mm",
          dateTimeFormatWithDayOfTheWeek: "ddd D MMM HH:mm",
          phoneCountryCode: 46,
          minimumPhoneLength: phoneLength_SE[0],
          phoneLength: phoneLength_SE[1],
          phoneLengthLandlineMin: landLineLength_SE[0],
          phoneLengthLandlineMax: landLineLength_SE[1],
          defaultMapCenterLatitude: 60.12816,
          defaultMapCenterLongitude: 18.6435,
          countryCode: "se"
        };
      case BiCountryId.DE:
        return {
          dateFormat: "DD/MM/YYYY",
          dateFormatNoYear: "DD/MM",
          dateTimeFormat: "DD/MM/YYYY HH:mm",
          dateTimeFormatWithDayOfTheWeek: "ddd. D. MMM. HH:mm",
          phoneCountryCode: 49,
          minimumPhoneLength: phoneLength_DE[0],
          phoneLength: phoneLength_DE[1],
          phoneLengthLandlineMin: phoneLength_DE[0],
          phoneLengthLandlineMax: phoneLength_DE[1],
          defaultMapCenterLatitude: 54.6683626,
          defaultMapCenterLongitude: 9.2664124,
          countryCode: "de"
        };
      case BiCountryId.EN:
        return {
          dateFormat: "DD/MM/YYYY",
          dateFormatNoYear: "DD/MM",
          dateTimeFormat: "DD/MM/YYYY HH:mm",
          dateTimeFormatWithDayOfTheWeek: "ddd, D MMM [at] HH:mm",
          phoneCountryCode: 44, // Change to England
          phoneLength: 8, // Change to England
          phoneLengthLandlineMin: 8, // Change to England
          phoneLengthLandlineMax: 10, // Change to England
          defaultMapCenterLatitude: 56.2639, // Change to England
          defaultMapCenterLongitude: 9.5018, // Change to England
          countryCode: "en"
        };
      case BiCountryId.FI:
        return {
          dateFormat: "DD/MM/YYYY",
          dateFormatNoYear: "DD/MM",
          dateTimeFormat: "DD/MM/YYYY HH:mm",
          dateTimeFormatWithDayOfTheWeek: "dd D.M. [klo] HH:mm",
          phoneCountryCode: 358,
          minimumPhoneLength: phoneLength_FI[0],
          phoneLength: phoneLength_FI[1],
          phoneLengthLandlineMin: landLineLength_FI[0],
          phoneLengthLandlineMax: landLineLength_FI[1],
          defaultMapCenterLatitude: 64.6233799,
          defaultMapCenterLongitude: 23.8364001,
          countryCode: "fi"
        };
      case BiCountryId.NO:
        return {
          dateFormat: "YYYY-MM-DD",
          dateFormatNoYear: "MM-DD",
          dateTimeFormat: "YYYY-MM-DD HH:mm",
          dateTimeFormatWithDayOfTheWeek: "ddd D. MMM., HH:mm",
          phoneCountryCode: 47,
          minimumPhoneLength: phoneLength_NO,
          phoneLength: phoneLength_NO,
          phoneLengthLandlineMin: phoneLength_NO,
          phoneLengthLandlineMax: phoneLength_NO,
          defaultMapCenterLatitude: 65.4465118,
          defaultMapCenterLongitude: 16.7209846,
          countryCode: "no"
        };
    }
  }

  public getCountryIdByPhoneCode(phoneCode: string) {
    switch (phoneCode) {
      case "+45":
        return BiCountryId.DK;
      case "+44":
        return BiCountryId.EN;
      case "+46":
        return BiCountryId.SE;
      case "+47":
        return BiCountryId.NO;
      case "+49":
        return BiCountryId.DE;
      case "+358":
        return BiCountryId.FI;
    }
  }

  public getCountryIdByLanguageCode(code: string) {
    switch (code) {
      case "dk":
        return BiCountryId.DK;
      case "se":
        return BiCountryId.SE;
      case "fi":
        return BiCountryId.FI;
      case "no":
        return BiCountryId.NO;
      case "en":
        return BiCountryId.EN;
      default:
        return BiCountryId.DK;
    }
  }

  public getLanguageIdByLanguageCode(code: string) {
    switch (code) {
      case "dk":
        return BiLanguageId.DK;
      case "se":
        return BiLanguageId.SE;
      case "fi":
        return BiLanguageId.FI;
      case "no":
        return BiLanguageId.NO;
      case "en":
        return BiLanguageId.EN;
      default:
        return BiLanguageId.DK;
    }
  }

  /**
   * Sets the language for the Ngx TranslateService.
   */
  public setLanguage(language: string) {
    this.translator.use(language);
  }
}
